import { DestroyRef, inject } from '@angular/core'
import { Auth, User } from '@angular/fire/auth'
import { untilDestroyed } from '@ngneat/until-destroy'
import { Observable } from 'rxjs'
import { Socket } from 'socket.io-client'
import { FIREBASE_AUTH_SDK } from './firebase-auth-sdk'
import { AuthService } from './auth.service'

/** 両トランスポートでトークン更新の購読と失効時の遷移を共有する。 */
export function setupSocketAuth(
  owner: object,
  getSocket: () => Socket | null,
  disconnect: () => void,
) {
  const sdk = inject(FIREBASE_AUTH_SDK)
  const firebaseAuth = inject(Auth)
  const auth = inject(AuthService)
  let generation = 0
  let notification = 0
  let reservation = 0
  let invalidated = false
  let destroyed = false
  let lastToken: string | undefined
  let timer: ReturnType<typeof setTimeout> | undefined
  const stop = () => {
    generation++
    reservation++
    clearTimeout(timer)
    lastToken = undefined
  }
  inject(DestroyRef).onDestroy(() => {
    destroyed = true
    stop()
  })
  const current = (socket: Socket, epoch: number) =>
    !destroyed &&
    !invalidated &&
    epoch === generation &&
    getSocket() === socket &&
    socket.connected
  const schedule = async (user: User, socket: Socket, epoch: number) => {
    if (!current(socket, epoch)) return
    const attempt = ++reservation
    clearTimeout(timer)
    try {
      const result = await sdk.getIdTokenResult(user)
      if (!current(socket, epoch) || attempt !== reservation) return
      const expires = Date.parse(result.expirationTime)
      if (!Number.isFinite(expires)) throw new Error('Invalid token expiration')
      timer = setTimeout(
        () => {
          if (!current(socket, epoch)) return
          const refreshNotification = ++notification
          void user
            .getIdToken(true)
            .then((token) => {
              // onIdTokenChanged と同じ送信経路に通し、同一トークンの二重送信を防ぐ。
              if (refreshNotification === notification)
                deliver(token, user, socket, epoch)
            })
            .catch(() => retry(user, socket, epoch))
        },
        Math.max(0, expires - Date.now() - 5 * 60 * 1000),
      )
    } catch {
      if (attempt === reservation) retry(user, socket, epoch)
    }
  }
  const retry = (user: User, socket: Socket, epoch: number) => {
    if (!current(socket, epoch)) return
    clearTimeout(timer)
    timer = setTimeout(() => {
      void schedule(user, socket, epoch)
    }, 30000)
  }
  const deliver = (
    token: string,
    user: User,
    socket: Socket,
    epoch: number,
  ) => {
    if (!current(socket, epoch) || token === lastToken) return
    lastToken = token
    socket.emit('auth:token', { token })
    void schedule(user, socket, epoch)
  }
  new Observable<User | null>((subscriber) =>
    sdk.onIdTokenChanged(firebaseAuth, (user) => subscriber.next(user)),
  )
    .pipe(untilDestroyed(owner))
    .subscribe((user) => {
      const attempt = ++notification
      const epoch = generation
      const socket = getSocket()
      if (!user) {
        stop()
        return
      }
      if (!socket || !current(socket, epoch)) return
      void user
        .getIdToken()
        .then((token) => {
          if (attempt === notification) deliver(token, user, socket, epoch)
        })
        .catch(() => retry(user, socket, epoch))
    })
  return {
    connected: (handshakeToken: string | undefined) => {
      stop()
      const socket = getSocket()
      const user = firebaseAuth.currentUser
      if (!socket || !user) return
      lastToken = handshakeToken
      const epoch = generation
      const attempt = ++notification
      void schedule(user, socket, epoch)
      void user
        .getIdToken()
        .then((token) => {
          if (attempt === notification) deliver(token, user, socket, epoch)
        })
        .catch(() => retry(user, socket, epoch))
    },
    stop,
    invalidated: (payload: { reason?: string }) => {
      if (invalidated) return
      invalidated = true
      stop()
      disconnect()
      if (payload?.reason === 'VERIFY_UNAVAILABLE') {
        auth.redirectToLogin({ error: 'verify_unavailable' })
        return
      }
      void auth
        .signOutPreservingReturnUrl()
        .then(() => auth.redirectToLogin({ error: 'session_invalidated' }))
        .catch(() => auth.redirectToLogin({ error: 'session_invalidated' }))
    },
  }
}
